import { Box, Grid, Heading, Text, Image, VStack, HStack, Icon } from '@chakra-ui/react';
import { Folder, Search, Plus, Circle, ArrowRight} from 'react-feather';
import { AuthProvider } from 'oidc-react';
import { useAuth } from 'oidc-react';
import { Link } from 'react-router-dom'; 

import { useLazyGetAllProjectStatesQuery } from '@/lib/api/GraphAPI';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { setProjectState } from '@/lib/state/slices/ProjectStateSlice';
import { useAppDispatch } from '@/lib/hooks';

import { useDisclosure } from "@chakra-ui/react";
import { NewProjectModal } from "@/components/NewProjectModal";

export default function HomePage() {
    return (
        <Box 
            w="100%"
            h="100%"
            overflowY="auto"
            overflowX="hidden"
        >
            <VStack 
                backgroundColor="background" 
                w="100%"
                p="50px"
                align="stretch"
                gap={50}
            > 
                <VStack align="start" w="100%">
                    <Heading color="#938AB6">
                        Dashboard
                    </Heading>

                    <HomeCard/>
                </VStack>

                <VStack align="start" w="100%">
                    <HStack w="100%" justify="space-between">
                        <HStack pl={5}>
                            <Icon 
                                as={Folder}
                                color="#ED435A"
                            />
                            <Heading color="#938AB6">
                                Projects
                            </Heading>
                        </HStack>
                        
                        <Link to="/projects">
                            <Heading 
                                color="#726AE9" 
                                fontSize="18px"
                                cursor="pointer"
                                _hover={{
                                    textDecoration:"underline"
                                }}
                            >
                                See all
                            </Heading>
                        </Link>
                        
                    </HStack>                    
                    <ProjectCards/>
                </VStack>

            </VStack>
        </Box>
    );
}

function HomeCard() {
    const auth = useAuth();
    const firstName = auth.userData?.profile?.given_name || "User";

    const items = [
        {name: "Projects", icon: Folder, text: "Manage Projects", path: "/projects"},
        {name: "Search", icon: Search, text: "Search for cases", path: "/research"}
    ];

    return (
        <HStack
                bg="white"
                w="100%"
                borderRadius="24px"
                justifyContent="space-between"
                p={10}
                align="start"
                >
                    <VStack
                        pt={3}
                        pl={8}
                    >
                        <VStack
                            align="start"
                            gap={4}
                        >
                            <Heading
                                fontSize="45px"
                                lineHeight={{base: "1"}}
                            >
                            Hi, {firstName}!
                            </Heading>

                            <Text
                                color="#938AB6"
                                fontSize="20px"
                            >
                                What do you want to do?
                            </Text>
                        </VStack>

                        <VStack
                            pt={20}
                            pl={2}
                            align="start"
                            gap={6}
                        >
                            {items.map((item, i) => {
                                const icon = item.icon;
                                const text = item.text
                                
                                return (
                                    <Link key={i} to={item.path}>
                                        <HStack 
                                            gap={3} 
                                            align="center" 
                                            cursor="pointer" 
                                            _hover={{color: "navButtonHover"}}
                                        >
                                            <Icon 
                                                as={icon}
                                                color={i === 0 ? "#ED435A" : "#726AE9"}
                                            />

                                            <Text fontSize="21px">
                                                {text}
                                            </Text>
                                        </HStack>
                                    </Link>
                                )
                            })}
                        </VStack>
                    </VStack>

                    <Image
                        src="/meshr_dashboard_logo.png"
                        pr={40}
                    />
            </HStack>
    );
}

function ProjectCards() {
    const plusIcon = Plus
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const newProjectModal = useDisclosure();

    const [
        getAllProjects,
        {
            data: projects,
            isSuccess,
            isLoading,
            isError,
        },
    ] = useLazyGetAllProjectStatesQuery();

    useEffect(() => {
        getAllProjects();
    }, []);
    
    return (
        <Box
            w="100%"
        >
            <HStack
                gap={6}
                flexWrap="nowrap"
                align="stretch"
            >
                <VStack
                    w={100}
                    h={240}
                    bg="logoPurple"
                    borderRadius="24px"
                    flexShrink={0}
                    align="center"
                    justify="center"
                    cursor="pointer"
                    _hover={{
                        bg: "navButtonHover"
                    }}
                    onClick={() => newProjectModal.setOpen(true)}
                >
                    <Icon 
                        as={plusIcon}
                        color="white"
                        boxSize="70%"
                    />
                </VStack>

                <HStack
                    overflowX="auto"
                    overflowY="hidden"
                    gap={8}
                >
                    {isLoading && (
                        <Text>Loading projects...</Text>
                    )}

                    {isError && (
                        <Text>Failed to load projects.</Text>
                    )}

                    {isSuccess &&
                        projects?.map((project) => {

                            const folders = Object.values(
                                project.json.documents.structure.FOLDER ?? {}
                            );

                            return (
                            <Box
                                key={project.json.id}
                                w="325px"
                                h="200px"
                                bg="white"
                                borderRadius="24px"
                                p={6}
                                flexShrink={0}
                                cursor="pointer"
                                transition="0.2s"
                                _hover={{
                                    transform: "translateY(-10px)",
                                    shadow: "md",
                                    bg: "cardHover"
                                }}
                                onClick={() => {
                                    dispatch(setProjectState(project.json));
                                    navigate("/research");
                                }}
                            >
                                <VStack
                                    align="start"
                                    h="100%"
                                    justify="space-between"
                                >
                                    <Heading size="xl" color="#726AE9">
                                        {project.json.name}
                                    </Heading>

                                    <VStack
                                        align="start"
                                        gap={3}
                                    >
                                        {folders.slice(0, 2).map((folder: any) => (
                                            <HStack key={folder.id}>
                                                <Box
                                                    w="8px"
                                                    h="8px"
                                                    borderRadius="full"
                                                    bg="secondaryText"
                                                />
                                                <Text color="secondaryText">
                                                    {folder.name}
                                                </Text>
                                            </HStack>
                                        ))}
                                    </VStack>
                                    

                                    <Text color="gray.500">
                                        Click to open
                                    </Text>
                                </VStack>
                            </Box>
                            );
                        })}
                </HStack>
            </HStack>

            <NewProjectModal
                open={newProjectModal.open}
                setOpen={newProjectModal.setOpen}
            />
        </Box>
    );
}